import { createAsyncThunk } from '@reduxjs/toolkit';

import { uploadImage } from '../api/upload';
import { TRootState } from '../reducers';
import { attachesSlice } from '../reducers/attaches';
import { IAttachment, IAttachmentError } from '../reducers/types';
import { selectAttaches } from '../selectors/attaches';

const LIMIT_ATTACH_IMAGES = 7;
const GIF_MAX_SIZE = 15 * 1024 * 1024;
const IMAGE_ATTACH_IMAGES = 25 * 1024 * 1024;

const FILE_SIZES_ARTICLE_URL =
  'https://support.wix.com/en/article/wix-media-supported-media-file-types-and-file-sizes?tabs=Images';

const limitError: IAttachmentError = {
  localKey: 'attach_error_limit',
};

const sizeFileError: IAttachmentError = {
  localKey: 'attach_error_size_file',
  learnMoreUrl: FILE_SIZES_ARTICLE_URL,
};

const sizeFilesError = (files: File[]): IAttachmentError => ({
  localKey: 'attach_error_size_files',
  fileNames: files.map((file) => file.name),
  learnMoreUrl: FILE_SIZES_ARTICLE_URL,
});

export const loadAttaches = createAsyncThunk<
  IAttachment[],
  File[],
  { state: TRootState; rejectValue: IAttachmentError }
>('loadAttaches', async (newAttaches, { rejectWithValue, dispatch, getState }) => {
  try {
    const { attachments } = selectAttaches(getState());
    const invalidAttaches: File[] = [];
    const validAttaches = newAttaches.filter((file: File) => {
      const isValid = isFileSizeValid(file);
      if (!isValid) {
        invalidAttaches.push(file);
      }
      return isValid;
    });

    // if more than allowed are attached - show error and do not upload new files
    if (attachments.length + newAttaches.length > LIMIT_ATTACH_IMAGES) {
      return rejectWithValue(limitError);
    }

    if (invalidAttaches.length > 0) {
      // if single file is attached and there is error with this file - show error and do not upload this file
      if (newAttaches.length === 1) {
        return rejectWithValue(sizeFileError);
      }

      // if some files are not attached due to error - show error message and a list of file names
      dispatch(attachesSlice.actions.setError(sizeFilesError(invalidAttaches)));
    }

    if (validAttaches.length === 0) {
      return [];
    }

    // use await before return to handle uploadImage errors in this try/catch
    return await uploadImage(validAttaches);
  } catch (err) {
    return rejectWithValue({
      localKey: newAttaches.length === 1 ? 'attach_error_file' : 'attach_error_files',
    });
  }
});

function isFileSizeValid({ type, size }: File): boolean {
  // MIME types are case-insensitive but are traditionally written in lowercase. Add 'toLowerCase' just to be more safe
  return type.toLowerCase() === 'image/gif' ? size < GIF_MAX_SIZE : size < IMAGE_ATTACH_IMAGES;
}
